/**
 * MinistriesManagement - Admin component for managing church ministries/departments
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Users,
  UserPlus,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Sparkles,
  Music,
  Heart,
  Shield,
  Video,
  BookOpen,
  Globe,
  MessageCircle,
  Coffee,
  Megaphone,
  ChevronRight,
  Target,
  Eye,
  Settings,
  Baby,
  GraduationCap,
  Mic
} from 'lucide-react';

// API functions
const ministriesAPI = {
  list: (params) => api.get('/admin/ministries', { params }),
  create: (data) => api.post('/admin/ministries', data),
  get: (id) => api.get(`/admin/ministries/${id}`),
  update: (id, data) => api.put(`/admin/ministries/${id}`, data),
  delete: (id) => api.delete(`/admin/ministries/${id}`),
  getTypes: () => api.get('/admin/ministries/types'),
  getRoles: () => api.get('/admin/ministries/roles'),
  getStats: (params) => api.get('/admin/ministries/stats', { params }),
  // Members
  listMembers: (ministryId) => api.get(`/admin/ministries/${ministryId}/members`),
  addMember: (ministryId, data) => api.post(`/admin/ministries/${ministryId}/members`, data),
  updateMember: (ministryId, membershipId, data) => api.put(`/admin/ministries/${ministryId}/members/${membershipId}`, data),
  removeMember: (ministryId, membershipId) => api.delete(`/admin/ministries/${ministryId}/members/${membershipId}`),
};

// Icon mapping for ministry types
const TYPE_ICONS = {
  youth: Sparkles,
  worship: Music,
  children: Baby,
  men: Shield,
  women: Heart,
  prayer: Heart,
  media: Video,
  evangelism: Megaphone,
  hospitality: Coffee,
  discipleship: BookOpen,
  counseling: MessageCircle,
  missions: Globe,
  education: GraduationCap,
  choir: Mic,
  custom: Settings
};

const MinistriesManagement = () => {
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [editingMinistry, setEditingMinistry] = useState(null);
  const [selectedMinistry, setSelectedMinistry] = useState(null);
  const [showMemberDialog, setShowMemberDialog] = useState(false);
  const [memberFormData, setMemberFormData] = useState({
    member_id: '',
    member_name: '',
    member_email: '',
    member_phone: '',
    role_id: 'member',
    team: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    ministry_type: 'custom',
    church_id: '',
    description: '',
    vision: '',
    mission: '',
    meeting_schedule: '',
    meeting_location: '',
    contact_email: '',
    contact_phone: '',
    is_active: true
  });

  // The units this admin covers — a ministry belongs to one of them.
  const { data: churches } = useQuery({
    queryKey: ['ministry-owner-churches'],
    queryFn: () => api.get('/admin/churches', { params: { limit: 500 } }).then(r => r.data)
  });

  // Queries
  const { data: ministries, isLoading } = useQuery({
    queryKey: ['ministries'],
    queryFn: () => ministriesAPI.list().then(r => r.data)
  });

  const { data: types } = useQuery({
    queryKey: ['ministry-types'],
    queryFn: () => ministriesAPI.getTypes().then(r => r.data)
  });

  const { data: roles } = useQuery({
    queryKey: ['ministry-roles'],
    queryFn: () => ministriesAPI.getRoles().then(r => r.data)
  });

  const { data: stats } = useQuery({
    queryKey: ['ministry-stats'],
    queryFn: () => ministriesAPI.getStats().then(r => r.data)
  });

  const { data: selectedMinistryData, isLoading: loadingDetails } = useQuery({
    queryKey: ['ministry', selectedMinistry],
    queryFn: () => ministriesAPI.get(selectedMinistry).then(r => r.data),
    enabled: !!selectedMinistry
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => ministriesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['ministries']);
      queryClient.invalidateQueries(['ministry-stats']);
      toast.success('Ministry created successfully');
      handleCloseDialog();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create ministry')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => ministriesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['ministries']);
      queryClient.invalidateQueries(['ministry', selectedMinistry]);
      toast.success('Ministry updated successfully');
      handleCloseDialog();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update ministry')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => ministriesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['ministries']);
      queryClient.invalidateQueries(['ministry-stats']);
      setSelectedMinistry(null);
      toast.success('Ministry deleted successfully');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete ministry')
  });

  const addMemberMutation = useMutation({
    mutationFn: ({ ministryId, data }) => ministriesAPI.addMember(ministryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['ministry', selectedMinistry]);
      queryClient.invalidateQueries(['ministries']);
      toast.success('Member added successfully');
      setShowMemberDialog(false);
      setMemberFormData({ member_id: '', member_name: '', member_email: '', member_phone: '', role_id: 'member', team: '' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to add member')
  });

  const removeMemberMutation = useMutation({
    mutationFn: ({ ministryId, membershipId }) => ministriesAPI.removeMember(ministryId, membershipId),
    onSuccess: () => {
      queryClient.invalidateQueries(['ministry', selectedMinistry]);
      queryClient.invalidateQueries(['ministries']);
      toast.success('Member removed');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to remove member')
  });

  const handleOpenCreate = () => {
    setEditingMinistry(null);
    setFormData({
      name: '',
      ministry_type: 'custom',
      church_id: '',
      description: '',
      vision: '',
      mission: '',
      meeting_schedule: '',
      meeting_location: '',
      contact_email: '',
      contact_phone: '',
      is_active: true
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (ministry) => {
    setEditingMinistry(ministry);
    setFormData({
      name: ministry.name || '',
      ministry_type: ministry.ministry_type || 'custom',
      church_id: ministry.church_id || '',
      description: ministry.description || '',
      vision: ministry.vision || '',
      mission: ministry.mission || '',
      meeting_schedule: ministry.meeting_schedule || '',
      meeting_location: ministry.meeting_location || '',
      contact_email: ministry.contact_email || '',
      contact_phone: ministry.contact_phone || '',
      is_active: ministry.is_active ?? true
    });
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingMinistry(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Ministry name is required');
      return;
    }
    
    if (editingMinistry) {
      updateMutation.mutate({ id: editingMinistry.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleAddMember = (e) => {
    e.preventDefault();
    if (!memberFormData.member_name.trim()) {
      toast.error('Member name is required');
      return;
    }
    
    addMemberMutation.mutate({
      ministryId: selectedMinistry,
      data: {
        ...memberFormData,
        member_id: memberFormData.member_id || `manual-${Date.now()}`
      }
    });
  };

  const getTypeColor = (typeId) => {
    const type = types?.find(t => t.id === typeId);
    return type?.color || '#6B7280';
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Ministries</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage church ministries, departments, and service teams
          </p>
        </div>
        <Button onClick={handleOpenCreate} data-testid="create-ministry-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Ministry
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_ministries || 0}</p>
                <p className="text-sm text-slate-500">Total Ministries</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_members || 0}</p>
                <p className="text-sm text-slate-500">Total Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Target className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.by_role?.head || 0}</p>
                <p className="text-sm text-slate-500">Ministry Heads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 rounded-lg">
                <Music className="h-5 w-5 text-pink-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.by_type?.worship || 0}</p>
                <p className="text-sm text-slate-500">Worship Teams</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ministry Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ministries?.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Sparkles className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No ministries yet</p>
              <p className="text-sm text-slate-500 mt-1">Create your first ministry to get started</p>
              <Button onClick={handleOpenCreate} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Create Ministry
              </Button>
            </CardContent>
          </Card>
        ) : (
          ministries?.map(ministry => {
            const Icon = TYPE_ICONS[ministry.ministry_type] || Sparkles;
            const color = getTypeColor(ministry.ministry_type);
            const isSelected = selectedMinistry === ministry.id;
            
            return (
              <Card
                key={ministry.id}
                className={`cursor-pointer hover:shadow-md transition-all ${
                  isSelected ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => setSelectedMinistry(ministry.id)}
                data-testid={`ministry-card-${ministry.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="h-5 w-5" style={{ color }} />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{ministry.name}</CardTitle>
                        <Badge 
                          variant="outline" 
                          className="text-xs mt-1"
                          style={{ borderColor: color, color }}
                        >
                          {types?.find(t => t.id === ministry.ministry_type)?.name || ministry.ministry_type}
                        </Badge>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenEdit(ministry); }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this ministry?')) {
                              deleteMutation.mutate(ministry.id);
                            }
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                    {ministry.description || 'No description'}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-slate-500">
                      <Users className="h-4 w-4" />
                      <span>{ministry.member_count || 0} members</span>
                    </div>
                    {ministry.head_name && (
                      <span className="text-slate-500 truncate max-w-[120px]">
                        Head: {ministry.head_name}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Ministry Detail Dialog */}
      <Dialog open={!!selectedMinistry && !showDialog} onOpenChange={() => setSelectedMinistry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {loadingDetails ? (
            <div className="space-y-4 p-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-32" />
            </div>
          ) : selectedMinistryData ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = TYPE_ICONS[selectedMinistryData.ministry_type] || Sparkles;
                    const color = getTypeColor(selectedMinistryData.ministry_type);
                    return (
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
                        <Icon className="h-6 w-6" style={{ color }} />
                      </div>
                    );
                  })()}
                  <div>
                    <DialogTitle className="text-xl">{selectedMinistryData.name}</DialogTitle>
                    <DialogDescription>{selectedMinistryData.description || 'No description'}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              
              <Tabs defaultValue="members" className="mt-4">
                <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3">
                  <TabsTrigger value="members">Members ({selectedMinistryData.member_count || 0})</TabsTrigger>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="vision">Vision & Mission</TabsTrigger>
                </TabsList>
                
                <TabsContent value="members" className="space-y-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-slate-500">Ministry members and roles</p>
                    <Button size="sm" onClick={() => setShowMemberDialog(true)}>
                      <UserPlus className="h-4 w-4 mr-1" />
                      Add Member
                    </Button>
                  </div>
                  
                  {selectedMinistryData.teams?.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      <span className="text-sm text-slate-500">Teams:</span>
                      {selectedMinistryData.teams.map(team => (
                        <Badge key={team} variant="outline">{team}</Badge>
                      ))}
                    </div>
                  )}
                  
                  {selectedMinistryData.members?.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No members yet. Add your first member!
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {selectedMinistryData.members?.map(member => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white">
                              <span className="text-sm font-medium">
                                {member.member_name?.[0]?.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{member.member_name}</p>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="text-xs">
                                  {roles?.find(r => r.id === member.role_id)?.name || member.role_id}
                                </Badge>
                                {member.team && (
                                  <span className="text-xs text-slate-500">{member.team}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700"
                            onClick={() => {
                              if (window.confirm(`Remove ${member.member_name}?`)) {
                                removeMemberMutation.mutate({
                                  ministryId: selectedMinistry,
                                  membershipId: member.id
                                });
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="details" className="space-y-4">
                  {selectedMinistryData.meeting_schedule && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span>{selectedMinistryData.meeting_schedule}</span>
                    </div>
                  )}
                  {selectedMinistryData.meeting_location && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span>{selectedMinistryData.meeting_location}</span>
                    </div>
                  )}
                  {selectedMinistryData.contact_email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <a href={`mailto:${selectedMinistryData.contact_email}`} className="text-blue-600 hover:underline">
                        {selectedMinistryData.contact_email}
                      </a>
                    </div>
                  )}
                  {selectedMinistryData.contact_phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <span>{selectedMinistryData.contact_phone}</span>
                    </div>
                  )}
                  <div className="pt-4">
                    <Button variant="outline" onClick={() => handleOpenEdit(selectedMinistryData)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Ministry
                    </Button>
                  </div>
                </TabsContent>
                
                <TabsContent value="vision" className="space-y-4">
                  {selectedMinistryData.vision ? (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Vision
                      </h4>
                      <p className="text-slate-600">{selectedMinistryData.vision}</p>
                    </div>
                  ) : null}
                  {selectedMinistryData.mission ? (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        Mission
                      </h4>
                      <p className="text-slate-600">{selectedMinistryData.mission}</p>
                    </div>
                  ) : null}
                  {!selectedMinistryData.vision && !selectedMinistryData.mission && (
                    <div className="text-center py-8 text-slate-500">
                      <p>No vision or mission statement set.</p>
                      <Button variant="outline" className="mt-2" onClick={() => handleOpenEdit(selectedMinistryData)}>
                        Add Vision & Mission
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Ministry Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMinistry ? 'Edit Ministry' : 'Create New Ministry'}
            </DialogTitle>
            <DialogDescription>
              {editingMinistry ? 'Update ministry details' : 'Add a new ministry to your organization'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Ministry Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Youth Ministry"
                data-testid="ministry-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="type">Ministry Type</Label>
              <Select
                value={formData.ministry_type}
                onValueChange={(val) => setFormData({ ...formData, ministry_type: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {types?.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: type.color }}
                        />
                        {type.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="ministry-church">Belongs to</Label>
              <Select
                value={formData.church_id || 'none'}
                onValueChange={(val) => setFormData({ ...formData, church_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger data-testid="ministry-church-select">
                  <SelectValue placeholder="Choose a church or province" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="none">No unit — platform wide</SelectItem>
                  {churches?.map(church => (
                    <SelectItem key={church.id} value={church.id}>
                      {church.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Only staff with authority over this unit will see the ministry.
              </p>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the ministry"
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="vision">Vision Statement</Label>
              <Textarea
                id="vision"
                value={formData.vision}
                onChange={(e) => setFormData({ ...formData, vision: e.target.value })}
                placeholder="What is the vision for this ministry?"
                rows={2}
              />
            </div>
            
            <div>
              <Label htmlFor="mission">Mission Statement</Label>
              <Textarea
                id="mission"
                value={formData.mission}
                onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                placeholder="What is the mission of this ministry?"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="schedule">Meeting Schedule</Label>
                <Input
                  id="schedule"
                  value={formData.meeting_schedule}
                  onChange={(e) => setFormData({ ...formData, meeting_schedule: e.target.value })}
                  placeholder="e.g., Every Friday 7PM"
                />
              </div>
              <div>
                <Label htmlFor="location">Meeting Location</Label>
                <Input
                  id="location"
                  value={formData.meeting_location}
                  onChange={(e) => setFormData({ ...formData, meeting_location: e.target.value })}
                  placeholder="e.g., Youth Hall"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Contact Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="ministry@church.org"
                />
              </div>
              <div>
                <Label htmlFor="phone">Contact Phone</Label>
                <Input
                  id="phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+1 555-0123"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="active">Active</Label>
                <p className="text-xs text-slate-500">Ministry is currently active</p>
              </div>
              <Switch
                id="active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showMemberDialog} onOpenChange={setShowMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Ministry Member</DialogTitle>
            <DialogDescription>Add a new member to this ministry</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddMember} className="space-y-4">
            <div>
              <Label htmlFor="member_name">Member Name *</Label>
              <Input
                id="member_name"
                value={memberFormData.member_name}
                onChange={(e) => setMemberFormData({ ...memberFormData, member_name: e.target.value })}
                placeholder="Full name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="member_email">Email</Label>
                <Input
                  id="member_email"
                  type="email"
                  value={memberFormData.member_email}
                  onChange={(e) => setMemberFormData({ ...memberFormData, member_email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label htmlFor="member_phone">Phone</Label>
                <Input
                  id="member_phone"
                  value={memberFormData.member_phone}
                  onChange={(e) => setMemberFormData({ ...memberFormData, member_phone: e.target.value })}
                  placeholder="+1 555-0123"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={memberFormData.role_id}
                  onValueChange={(val) => setMemberFormData({ ...memberFormData, role_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles?.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="team">Team (optional)</Label>
                <Input
                  id="team"
                  value={memberFormData.team}
                  onChange={(e) => setMemberFormData({ ...memberFormData, team: e.target.value })}
                  placeholder="e.g., Vocals, Tech"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMemberDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addMemberMutation.isPending}>
                {addMemberMutation.isPending ? 'Adding...' : 'Add Member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MinistriesManagement;
